// WS-12: compound buildings. A hero building (reception, restaurant, lodge) is decomposed into PARTS
// (wings, halls, porches), each with its own plan, wall height, roof and openings, all taken from photos
// and an annotated plan. Resort-agnostic: everything comes from resort.json
//   buildings.<key> = { model: "compound", params: { parts: [...], chimneys, roofBoxes, signs } }
// Coordinates are the resort's local metres (x east, z south), so parts can be traced on the
// ?edit&ortho underlay; y = 0 is the footprint base. Nothing is added unless a param asks for it:
// a part has no windows, doors, chimneys, crown or sign by default (no invented features).
//
// Part:
//   { id, place?, pts: [[x, z], ...] (convex) | shape: { type: "octagon", center: [x, z], apothem, rot },
//     wallH, wall: "wood" | <material key>, openEdges: [edge],      // edges with no wall (open porch; a gable stays)
//     lowEdges: { <edge>: h }, posts: true, floor: true | h,           // railings, corner posts, a deck floor
//     roof: { type: "hip" | "gable", pitch (rise/run), overhang, gables: [edge], snow, capR },  // capR: flat top (crown seat)
//     view: "N" | <bearing> | { bearing, elevation, distance },       // the place's front: fly-to looks from this side
//     openings: [{ edge | facing: "N".."NW", from, to (m along the edge) | at, w,
//                  kind: "window" | "door" | "glassDoor", sill, head, panes, frame: "dark" | "light" | "white",
//                  look: "interior" (large glazing: lit interior behind glass),
//                  inside: "open" | "glow" (what is really behind the glass. "open" = real glass in a real hole
//                    in the wall, with the building's interior model behind it (params.interior, built with
//                    tools/blender/interior_kit.py); with no model loaded it falls back to "glow" = lit, nothing
//                    claimed. Without `inside`: the generic room, which only suits small windows),
//                  lamp, landing: depth, canopy: { depth, posts } }],
//                  kind: "bay" = an open doorway with no door leaf (a garage or shed standing open, photos):
//                    a real hole from the ground to `head`, `chamfer` (m) cuts its top corners, framed in `frame`
//     inside: { lit: true | false, glow, mat, floor }               // a part with a bay gets an inside: lining, floor
//                                                                    // and a flat ceiling at wallH; lit = light boards
//                                                                    // with `glow` (0.35) x the interior night glow,
//                                                                    // else dark (M.post);
//                                                                    // floor: a material key (default: the lining's)
//     lamps: [{ at: [x, z], y, r }],                                 // ceiling lamp (shade + lit lens facing down);
//                                                                    // the lens is an M.lamp part, so it is one of
//                                                                    // the map's lights (Fine-tune Lights, cast light)
//     crown: { h, r0, r1, slats },                                   // kota-style crown at the roof apex
//     labelAt: [x, z] }
// Building extras (anchored on the highest roof under the point):
//   chimneys: [{ at, r, h, collar }], roofBoxes: [{ at, size: [sx, sy, sz], rot }],
//   signs: [{ at, facing, w, h, lift, lines: [...], color, panel }]
//   fences: [{ pts: [[x, z], ...], closed, style, height, rails, spacing, src }]  // on the ground (rides.js buildFence)
// Edge i runs from pts[i] to pts[i + 1]. "facing" picks the exterior edge whose outward normal is
// closest to that compass direction (N = -z, E = +x).
import * as THREE from 'three/webgpu';
import { mergeGeometries } from 'three/addons/utils/BufferGeometryUtils.js';
import { Kit, insetConvex } from './units.js';
import { obb } from './buildings.js';
import { buildFence } from './rides.js';
import { derivedMaterials } from './materials.js';

const S2 = Math.SQRT1_2;
const COMPASS = { N: [0, -1], NE: [S2, -S2], E: [1, 0], SE: [S2, S2], S: [0, 1], SW: [-S2, S2], W: [-1, 0], NW: [-S2, -S2] };
const compassVec = (c) => (typeof c === 'number' ? [Math.sin((c * Math.PI) / 180), -Math.cos((c * Math.PI) / 180)] : COMPASS[c] || COMPASS.N);

/** Edges of a ring with unit direction, length and inward normal (same sign rule as insetConvex). */
function ring(pts) {
  const n = pts.length;
  let area = 0;
  for (let i = 0; i < n; i++) area += pts[i][0] * pts[(i + 1) % n][1] - pts[(i + 1) % n][0] * pts[i][1];
  const s = area > 0 ? 1 : -1;
  return pts.map((a, i) => {
    const b = pts[(i + 1) % n];
    const dx = b[0] - a[0], dz = b[1] - a[1], len = Math.hypot(dx, dz) || 1;
    return { a, b, len, d: [dx / len, dz / len], nIn: [(-dz / len) * s, (dx / len) * s] };
  });
}
const distIn = (e, p) => (p[0] - e.a[0]) * e.nIn[0] + (p[1] - e.a[1]) * e.nIn[1];

/** Sutherland-Hodgman: keep the part of a convex polygon where g(p) <= 0 (g linear). */
function clip(poly, g) {
  const out = [];
  for (let k = 0; k < poly.length; k++) {
    const P = poly[k], Q = poly[(k + 1) % poly.length];
    const gp = g(P), gq = g(Q);
    if (gp <= 0) out.push(P);
    if ((gp < 0 && gq > 0) || (gp > 0 && gq < 0)) {
      const t = gp / (gp - gq);
      out.push([P[0] + (Q[0] - P[0]) * t, P[1] + (Q[1] - P[1]) * t]);
    }
  }
  return out;
}

function octagonPts({ center: [cx, cz], apothem, rot = 0 }) {
  const R = apothem / Math.cos(Math.PI / 8), r0 = (rot * Math.PI) / 180;
  // Edge k (vertex k -> k + 1) has its outward normal at angle (k + 1) * 45 deg + rot from +x towards +z.
  return Array.from({ length: 8 }, (_, k) => {
    const a = Math.PI / 8 + (k * Math.PI) / 4 + r0;
    return [cx + Math.cos(a) * R, cz + Math.sin(a) * R];
  });
}

/**
 * Exact hip / gable / pyramid roof of a convex plan: each eave edge owns the region of the (overhang-
 * expanded) plan where it is the nearest eave line, lifted by pitch x distance. Gable edges take no face.
 */
function roofOf(pts, wallH, { pitch = 0.5, overhang = 0.6, gables = [], capR = 0 } = {}) {
  const outer = insetConvex(pts, -overhang);
  const E = ring(outer);
  const eaves = E.map((_, i) => i).filter((i) => !gables.includes(i));
  const eaveY = wallH - pitch * overhang;
  // capR > 0 truncates a pyramid/hip roof where the remaining top has that apothem: a flat cap for a crown,
  // collar or vent (a sharp apex under a crown looks like a spike; photos show the roof meeting the crown ring).
  let dMax = 0;
  for (const p of outer) for (const q of outer) dMax = Math.max(dMax, Math.min(...eaves.map((j) => distIn(E[j], [(p[0] + q[0]) / 2, (p[1] + q[1]) / 2]))));
  const cx = outer.reduce((a, p) => a + p[0] / outer.length, 0), cz = outer.reduce((a, p) => a + p[1] / outer.length, 0);
  dMax = Math.max(dMax, Math.min(...eaves.map((j) => distIn(E[j], [cx, cz]))));
  const dCut = capR > 0 ? Math.max(0, dMax - capR) : Infinity;
  const heightAt = (p) => eaveY + pitch * Math.min(dCut, ...eaves.map((j) => distIn(E[j], p)));
  const faces = [];
  for (const i of eaves) {
    let poly = outer;
    for (const j of eaves) if (j !== i) poly = clip(poly, (p) => distIn(E[i], p) - distIn(E[j], p));
    if (dCut !== Infinity) poly = clip(poly, (p) => distIn(E[i], p) - dCut);
    if (poly.length >= 3) faces.push({ edge: i, poly: poly.map((p) => [p[0], eaveY + pitch * distIn(E[i], p), p[1]]) });
  }
  if (dCut !== Infinity && gables.length === 0) {
    const cap = insetConvex(outer, dCut);
    faces.push({ edge: -1, poly: cap.map((p) => [p[0], eaveY + pitch * dCut, p[1]]) });
  }
  let top = eaveY;
  for (const f of faces) for (const p of f.poly) top = Math.max(top, p[1]);
  const edgeOf = (p, q) => E.find((e) => Math.abs(distIn(e, [p[0], p[2]])) < 1e-3 && Math.abs(distIn(e, [q[0], q[2]])) < 1e-3);
  return { faces, top, eaveY, heightAt, outer, edgeOf };
}

const inside = (pts, p) => ring(pts).every((e) => distIn(e, p) >= -1e-6);

// Triangles with a guaranteed facing: each is flipped if its normal (three's (c-b)x(a-b)) points away from `want`.
// Front-side picking, lighting, shadows and AO all rely on outward/upward winding (round 4c: roofs faced down).
function triW(kit, mat, list, want) {
  kit.tris(mat, list.map(([a, b, c]) => {
    const ux = c[0] - b[0], uy = c[1] - b[1], uz = c[2] - b[2], vx = a[0] - b[0], vy = a[1] - b[1], vz = a[2] - b[2];
    const nx = uy * vz - uz * vy, ny = uz * vx - ux * vz, nz = ux * vy - uy * vx;
    return nx * want[0] + ny * want[1] + nz * want[2] < 0 ? [a, c, b] : [a, b, c];
  }));
}
const quadW = (kit, mat, a, b, c, d, want) => triW(kit, mat, [[a, b, c], [a, c, d]], want);
const UP = [0, 1, 0];
const outOf = (e) => [-e.nIn[0], 0, -e.nIn[1]];

function fan(poly) { const t = []; for (let k = 1; k < poly.length - 1; k++) t.push([poly[0], poly[k], poly[k + 1]]); return t; }

function addRoof(kit, M, r, snow = 0.32) {
  for (const f of r.faces) {
    triW(kit, M.roofEdge, fan(f.poly), UP);
    triW(kit, M.snow, fan(f.poly.map((p) => [p[0], p[1] + snow, p[2]])), UP);
    for (let k = 0; k < f.poly.length; k++) {
      const P = f.poly[k], Q = f.poly[(k + 1) % f.poly.length];
      const e = r.edgeOf(P, Q);
      if (!e) continue; // only the outer rim gets a fascia and a snow edge
      quadW(kit, M.roofEdge, [P[0], P[1] - 0.22, P[2]], [Q[0], Q[1] - 0.22, Q[2]], Q, P, outOf(e));
      quadW(kit, M.snow, P, Q, [Q[0], Q[1] + snow, Q[2]], [P[0], P[1] + snow, P[2]], outOf(e));
    }
  }
}

/** Wall of one edge from y0 up to h, with rectangular holes [from, to, sill, head] (see-through windows). */
function wallWithHoles(kit, mat, e, y0, h, holes) {
  const P = (u, y) => [e.a[0] + e.d[0] * u, y, e.a[1] + e.d[1] * u];
  const rect = (u0, u1, v0, v1) => { if (u1 - u0 > 1e-3 && v1 - v0 > 1e-3) quadW(kit, mat, P(u0, v0), P(u1, v0), P(u1, v1), P(u0, v1), outOf(e)); };
  let u = 0;
  for (const [f, t, sill, head] of [...holes].sort((p, q) => p[0] - q[0])) { rect(u, f, y0, h); rect(f, t, y0, sill); rect(f, t, head, h); u = t; }
  rect(u, e.len, y0, h);
}
/** Where a window sits on its edge: [from, to, sill, head] (the same numbers opening() draws). */
function windowRect(e, o, wallH) {
  const from = o.from ?? (o.at != null ? o.at - (o.w || 1) / 2 : 0), to = o.to ?? (o.at != null ? o.at + (o.w || 1) / 2 : e.len);
  return [Math.max(0, from), Math.min(e.len, to), o.sill ?? 0.6, Math.min(o.head ?? 2.6, wallH - 0.15)];
}

/** Frame bars + panes or a door on one wall edge. `see` = the building has an interior model: `inside: "open"`
 *  windows are real glass (their wall is cut in compound()). */
function opening(kit, M, e, o, wallH, see) {
  const len = e.len;
  const from = o.from ?? (o.at != null ? o.at - (o.w || 1) / 2 : 0);
  const to = o.to ?? (o.at != null ? o.at + (o.w || 1) / 2 : len);
  const w = Math.max(0.2, Math.min(len, to) - Math.max(0, from)), mid = (Math.max(0, from) + Math.min(len, to)) / 2;
  const nOut = [-e.nIn[0], -e.nIn[1]];
  const ry = Math.atan2(-e.d[1], e.d[0]);
  const at = (along, out) => [e.a[0] + e.d[0] * along + nOut[0] * out, e.a[1] + e.d[1] * along + nOut[1] * out];
  const frameMat = { dark: M.post, light: M.pine, white: M.trim }[o.frame || 'dark'] || M.post;
  const kind = o.kind || 'window';
  if (kind === 'door' || kind === 'glassDoor') {
    const h = o.head || 2.1;
    const [x, z] = at(mid, 0.05);
    kit.box(kind === 'glassDoor' ? M.windowWarm : (M[o.mat] || M.pine), x, h / 2, z, w, h, 0.06, ry);
    const [fx, fz] = at(mid, 0.08);
    kit.box(frameMat, fx, h + 0.06, fz, w + 0.16, 0.12, 0.1, ry);
    for (const s of [-1, 1]) { const [px, pz] = at(mid + s * (w / 2 + 0.04), 0.08); kit.box(frameMat, px, h / 2, pz, 0.08, h, 0.1, ry); }
    if (o.lamp) { const [lx, lz] = at(mid - w / 2 - 0.45, 0.14); kit.box(M.lamp, lx, h + 0.1, lz, 0.18, 0.28, 0.18, ry); }
    if (o.landing) { const [dx, dz] = at(mid, o.landing / 2); kit.box(M.deck, dx, 0.12, dz, w + 1.4, 0.24, o.landing, ry); }
    if (o.canopy) {
      const d = o.canopy.depth || 1.8, cw = o.canopy.w || w + 1.6, cy = h + 0.55;
      const [cx, cz] = at(mid, d / 2);
      kit.box(M.roofEdge, cx, cy, cz, cw, 0.22, d, ry);
      kit.box(M.snow, cx, cy + 0.24, cz, cw, 0.26, d, ry);
      for (const s of (o.canopy.posts ?? 2) === 1 ? [1] : (o.canopy.posts ?? 2) === 0 ? [] : [-1, 1]) {
        const [px, pz] = at(mid + s * (cw / 2 - 0.15), d - 0.15);
        kit.box(M.post, px, cy / 2, pz, 0.18, cy, 0.18, ry);
      }
      const [lx, lz] = at(mid, d * 0.5);
      kit.box(M.lamp, lx, cy - 0.16, lz, 0.4, 0.06, 0.4, ry); // ceiling light under the canopy
    }
    return;
  }
  const sill = o.sill ?? 0.6, head = Math.min(o.head ?? 2.6, wallH - 0.15), h = head - sill;
  const [x, z] = at(mid, 0.05);
  if (M.look) {
    // WS-37 (materials look): one interior-mapped pane (room depth, night gradient, day reflection) recessed behind
    // a deeper frame; the room's ceiling sits above the window head (tall halls get tall rooms).
    // `inside` (WS-37 r3) says what is really behind the glass: "open" = real glass, the interior model shows
    // through the hole compound() cut; any `inside` with no model = a plain glow. Without `inside`: the generic room.
    const [gx, gz] = at(mid, 0.03);
    if (see && o.inside === 'open') kit.box(M.glass, gx, sill + h / 2, gz, w, h, 0.02, ry);
    else kit.box(M.windowWarm, gx, sill + h / 2, gz, w, h, 0.04, ry).room(0, Math.max(3.0, head + 0.5), !!o.inside);
    const d = M.look.frame, fo2 = 0.05 + d / 2;
    for (const yy of [sill, head]) { const [fx, fz] = at(mid, fo2); kit.box(frameMat, fx, yy, fz, w + 0.14, 0.11, d, ry); }
    const panes = Math.max(1, o.panes || 1);
    for (let k = 0; k <= panes; k++) {
      const [px, pz] = at(mid - w / 2 + (w * k) / panes, fo2);
      kit.box(frameMat, px, sill + h / 2, pz, k === 0 || k === panes ? 0.11 : 0.07, h, k === 0 || k === panes ? d : d * 0.7, ry);
    }
    return;
  }
  if (see && o.inside === 'open') {
    const [gx, gz] = at(mid, 0.07);
    kit.box(M.glass, gx, sill + h / 2, gz, w, h, 0.02, ry);
  } else if (o.look === 'interior') {
    // Large glazing: lit interior (warm wood) behind reflective glass, so it reads as a window by day
    // and glows at night without blooming out like a light panel.
    // (walls are solid quads, so the 'interior' panel sits just in front of the wall, under the glass)
    const [ix, iz] = at(mid, 0.03);
    kit.box(M.interior, ix, sill + h / 2, iz, w, h, 0.02, ry);
    const [gx, gz] = at(mid, 0.07);
    kit.box(M.glass, gx, sill + h / 2, gz, w, h, 0.02, ry);
  } else kit.box(M.windowWarm, x, sill + h / 2, z, w, h, 0.05, ry);
  const fo = 0.09;
  for (const yy of [sill, head]) { const [fx, fz] = at(mid, fo); kit.box(frameMat, fx, yy, fz, w + 0.12, 0.1, 0.1, ry); }
  const panes = Math.max(1, o.panes || 1);
  for (let k = 0; k <= panes; k++) {
    const [px, pz] = at(mid - w / 2 + (w * k) / panes, fo);
    kit.box(frameMat, px, sill + h / 2, pz, k === 0 || k === panes ? 0.1 : 0.07, h, 0.1, ry);
  }
}

/** Where an open bay sits on its edge: [from, to, sill, head], the sill below the wall foot (no threshold wall). */
function bayRect(e, o, wallH) {
  const from = o.from ?? (o.at != null ? o.at - (o.w || 3) / 2 : 0), to = o.to ?? (o.at != null ? o.at + (o.w || 3) / 2 : e.len);
  return [Math.max(0, from), Math.min(e.len, to), -0.8, Math.min(o.head ?? 3.2, wallH - 0.15)];
}
/** An open bay: the wall that fills its chamfered top corners, the reveals through the wall (`depth` = the inside
 *  lining's inset, 0 = none) and a trim frame on the outside. */
function bay(kit, M, e, o, [from, to, , head], wallMat, depth) {
  const at = (u, y, out = 0) => [e.a[0] + e.d[0] * u - e.nIn[0] * out, y, e.a[1] + e.d[1] * u - e.nIn[1] * out];
  const c = Math.max(0, Math.min(o.chamfer || 0, (to - from) / 2 - 0.2, head - 0.6));
  const outN = outOf(e), down = [0, -1, 0];
  if (c > 0) for (const [u0, u1] of [[from, from + c], [to, to - c]]) {
    for (const out of depth ? [0, -depth] : [0]) triW(kit, wallMat, [[at(u0, head - c, out), at(u0, head, out), at(u1, head, out)]], out ? [-outN[0], 0, -outN[2]] : outN);
  }
  if (depth) { // reveals: jambs, chamfer soffits and the head, through the wall's thickness
    const rq = (p, q, n) => quadW(kit, wallMat, at(...p, 0), at(...q, 0), at(...q, -depth), at(...p, -depth), n);
    rq([from, -0.1], [from, head - c], [e.d[0], 0, e.d[1]]);
    rq([to, -0.1], [to, head - c], [-e.d[0], 0, -e.d[1]]);
    rq([from + c, head], [to - c, head], down);
    if (c > 0) { rq([from, head - c], [from + c, head], down); rq([to - c, head], [to, head - c], down); }
  }
  const fm = { dark: M.post, light: M.pine, white: M.trim }[o.frame || 'dark'] || M.post, w = 0.14, t = 0.06;
  const strip = (u0, y0, u1, y1) => { // a trim board on the facade from (u0, y0) to (u1, y1), w wide
    const L = Math.hypot(u1 - u0, y1 - y0), du = (u1 - u0) / L, dy = (y1 - y0) / L, pu = -dy * w / 2, py = du * w / 2;
    kit.slab(fm, at(u0 - pu, y0 - py, t), at(u1 - pu, y1 - py, t), at(u1 + pu, y1 + py, t), at(u0 + pu, y0 + py, t), t);
  };
  strip(from - w / 2, 0, from - w / 2, head - c);
  strip(to + w / 2, 0, to + w / 2, head - c);
  if (c > 0) { strip(from - w / 2, head - c, from + c, head + w / 2); strip(to - c, head + w / 2, to + w / 2, head - c); }
  strip(from + c - (c > 0 ? 0 : w), head + w / 2, to - c + (c > 0 ? 0 : w), head + w / 2);
}
/** The inside of an open bay: light boards that take the village lights' night ramp at `gain` x the interior glow
 *  (the full glow reads as a light box; the ceiling lamp is the brightest thing inside). One material per gain. */
function insideLit(M, gain) {
  M._insideMats ||= new Map();
  if (!M._insideMats.has(gain)) {
    const m = M.interior.clone();
    m.color.set(0xb8956a);
    m.userData.baseKey = 'interior'; m.userData.gain = gain;
    m.emissiveIntensity = M.interior.emissiveIntensity * gain;
    derivedMaterials.push(m);
    M._insideMats.set(gain, m);
  }
  return M._insideMats.get(gain);
}
/** Ceiling lamp: rod, dark shade and a lit lens facing down (the lens is the light the map lists). */
function ceilingLamp(kit, M, { at: [x, z], y, r = 0.32 }, ceil) {
  const ly = y ?? ceil - 0.7;
  kit.box(M.metal, x, (ly + 0.28 + ceil) / 2, z, 0.035, Math.max(0.05, ceil - ly - 0.28), 0.035);
  kit.cyl(M.roofEdge, x, ly + 0.14, z, 0.09, r, 0.28, 16);
  kit.cyl(M.lamp, x, ly - 0.01, z, r * 0.88, r * 0.88, 0.02, 16);
}
/** A fence group with its instanced posts written out as plain geometry, so it bakes with the building. */
function plainFence(group) {
  const out = new THREE.Group(), m4 = new THREE.Matrix4();
  group.traverse((m) => {
    if (!m.isMesh) return;
    if (!m.isInstancedMesh) { out.add(new THREE.Mesh(m.geometry, m.material)); return; }
    const geos = [];
    for (let i = 0; i < m.count; i++) { m.getMatrixAt(i, m4); geos.push(m.geometry.clone().applyMatrix4(m4)); }
    if (geos.length) out.add(new THREE.Mesh(mergeGeometries(geos), m.material));
  });
  return out;
}

function crown(kit, M, [cx, cz], y0, { h = 2.4, r0 = 0.6, r1 = 1.15, slats = 16 } = {}) {
  kit.cyl(M.pine, cx, y0 + 0.2, cz, r0 + 0.12, r0 + 0.12, 0.4, 16);
  for (let k = 0; k < slats; k++) {
    // Flat boards (photos: wide slats, cut tips of two lengths), flaring outwards from the base ring.
    const a = (k / slats) * Math.PI * 2, c = Math.cos(a), s = Math.sin(a), tx = -s, tz = c;
    const tip = h * (k % 2 ? 0.88 : 1), bw = ((2 * Math.PI * r0) / slats) * 0.42;
    const B = [cx + c * r0, y0 + 0.15, cz + s * r0], T = [cx + c * r1, y0 + tip, cz + s * r1];
    const w0 = bw, w1 = bw * (r1 / r0) * 0.8;
    kit.slab(M.pine, [B[0] - tx * w0, B[1], B[2] - tz * w0], [B[0] + tx * w0, B[1], B[2] + tz * w0], [T[0] + tx * w1, T[1], T[2] + tz * w1], [T[0] - tx * w1, T[1], T[2] - tz * w1], 0.05);
  }
  kit.cyl(M.interior, cx, y0 + h * 0.35, cz, r0 * 0.75, r0 * 0.7, h * 0.45, 12); // warm light from inside (photos: the slats glow at dusk)
}

function signMesh(M, s, y) {
  const lines = s.lines || [];
  const w = s.w || 4, hh = s.h || 1.4;
  const c = document.createElement('canvas');
  c.width = 512; c.height = Math.max(64, Math.round((512 * hh) / w));
  const g = c.getContext('2d');
  g.fillStyle = s.panel || '#2a1d17'; g.fillRect(0, 0, c.width, c.height);
  g.strokeStyle = 'rgba(255,210,150,0.35)'; g.lineWidth = 6; g.strokeRect(3, 3, c.width - 6, c.height - 6);
  g.fillStyle = s.color || '#ffd08a'; g.textAlign = 'center'; g.textBaseline = 'middle';
  const fs = Math.min((c.height * 0.78) / Math.max(1, lines.length), 120);
  g.font = `700 ${fs}px system-ui, sans-serif`;
  lines.forEach((t, i) => g.fillText(t, c.width / 2, c.height / 2 + (i - (lines.length - 1) / 2) * fs * 1.02, c.width * 0.9));
  const tex = new THREE.CanvasTexture(c);
  tex.colorSpace = THREE.SRGBColorSpace;
  const mat = new THREE.MeshStandardNodeMaterial({ map: tex, emissiveMap: tex, emissive: 0xffffff, roughness: 0.6, side: THREE.DoubleSide });
  // Lit with the other building lights: follows M.lamp, which the time of day drives.
  Object.defineProperty(mat, 'emissiveIntensity', { get: () => 0.05 + 0.2 * M.lamp.emissiveIntensity, set() {} });
  const geo = new THREE.PlaneGeometry(w, hh);
  const [fx, fz] = compassVec(s.facing ?? 'N');
  geo.rotateY(Math.atan2(fx, fz));
  geo.translate(s.at[0], y + hh / 2, s.at[1]);
  return new THREE.Mesh(geo, mat);
}

/**
 * @returns {{ places: { key, group, anchor: [x, y, z], frame: { L, W } }[], extras: THREE.Group, top: number }}
 */
export function compound(M, { parts = [], chimneys = [], roofBoxes = [], signs = [], fences = [] } = {}, { see = false, ground = () => 0 } = {}) {
  // see = the building's interior model is loaded (params.interior, main.js): windows marked `inside: "open"` become
  // real glass and their wall gets a real hole, so the model shows through.
  // ground(x, z) = the terrain height relative to the footprint base (fences stand on it).
  const built = parts.map((p) => {
    const pts = p.shape?.type === 'octagon' ? octagonPts(p.shape) : p.pts;
    const wallH = p.wallH || 3;
    const roofCfg = p.roof || {};
    const r = roofOf(pts, wallH, roofCfg);
    return { p, pts, wallH, r, edges: ring(pts) };
  });
  const roofAt = (x, z) => {
    let y = 0;
    for (const b of built) if (inside(b.r.outer, [x, z])) y = Math.max(y, b.r.heightAt([x, z]) + (b.p.roof?.snow ?? 0.32));
    return y;
  };
  const byPlace = new Map();
  for (const b of built) {
    const { p, pts, wallH, r, edges } = b;
    const kit = new Kit();
    const wallMat = M[p.wall] || M.wood;
    const gables = p.roof?.gables || [];
    const edgeOf = (o) => {
      if (!o.facing) return edges[o.edge];
      const v = compassVec(o.facing);
      return edges.reduce((best, c) => ((-c.nIn[0] * v[0] - c.nIn[1] * v[1]) > (-best.nIn[0] * v[0] - best.nIn[1] * v[1]) ? c : best), edges[0]);
    };
    const holes = new Map(); // edge -> see-through windows and open bays
    if (see) for (const o of p.openings || []) {
      const e = edgeOf(o);
      if (e && o.inside === 'open' && (o.kind || 'window') === 'window') holes.set(e, [...(holes.get(e) || []), windowRect(e, o, wallH)]);
    }
    const bays = (p.openings || []).filter((o) => o.kind === 'bay').map((o) => ({ o, e: edgeOf(o) })).filter((b) => b.e);
    for (const b of bays) { b.rect = bayRect(b.e, b.o, wallH); holes.set(b.e, [...(holes.get(b.e) || []), b.rect]); }
    edges.forEach((e, i) => {
      const open = (p.openEdges || []).includes(i), low = p.lowEdges?.[i];
      if (!open) wallWithHoles(kit, wallMat, e, -0.8, low ?? wallH, holes.get(e) || []);
      if (gables.includes(i)) {
        // Gable triangle: from the wall top up to the roof profile along this edge.
        const prof = [];
        for (let k = 0; k <= 16; k++) { const t = k / 16; const x = e.a[0] + (e.b[0] - e.a[0]) * t, z = e.a[1] + (e.b[1] - e.a[1]) * t; prof.push([x, Math.max(wallH, r.heightAt([x, z])) - 0.05, z]); }
        triW(kit, wallMat, fan([[e.a[0], wallH, e.a[1]], ...prof, [e.b[0], wallH, e.b[1]]]), outOf(e));
      }
    });
    if (p.posts) for (const [x, z] of pts) kit.box(M.post, x, wallH / 2, z, 0.18, wallH, 0.18); // open porch / canopy posts
    if (p.floor) kit.prism(M.deck, pts, 0, p.floor === true ? 0.2 : p.floor);
    addRoof(kit, M, r, p.roof?.snow ?? 0.32);
    for (const o of p.openings || []) {
      const e = edgeOf(o);
      if (e && o.kind !== 'bay') opening(kit, M, e, o, wallH, see);
    }
    // Open bays (garage or shed standing open): the part gets an inside, lined with the same holes, so the bay shows
    // a room (lit = the warm interior glow) instead of the back of the far wall. Nothing inside is invented: a plain
    // lining, floor and ceiling.
    const T = 0.18, inner = bays.length ? insetConvex(pts, T) : null;
    if (inner) {
      const inMat = M[p.inside?.mat] || (p.inside?.lit === false ? M.post : insideLit(M, p.inside?.glow ?? 0.35)), iEdges = ring(inner);
      const floorY = Math.max(0, ...inner.map(([x, z]) => ground(x, z))) + 0.03; // above the terrain, which runs on under the building
      iEdges.forEach((ie, i) => {
        if ((p.openEdges || []).includes(i)) return;
        const e = edges[i], shift = (ie.a[0] - e.a[0]) * e.d[0] + (ie.a[1] - e.a[1]) * e.d[1];
        const ih = bays.filter((b) => b.e === e).map((b) => [b.rect[0] - shift, b.rect[1] - shift, -0.1, b.rect[3]]);
        wallWithHoles(kit, inMat, { ...ie, nIn: [-ie.nIn[0], -ie.nIn[1]] }, floorY, wallH - 0.02, ih); // faces inwards
      });
      triW(kit, M[p.inside?.floor] || inMat, fan(inner.map(([x, z]) => [x, floorY, z])), UP);
      triW(kit, inMat, fan(inner.map(([x, z]) => [x, wallH - 0.02, z])), [0, -1, 0]);
    }
    for (const b of bays) bay(kit, M, b.e, b.o, b.rect, wallMat, inner ? T : 0);
    for (const l of p.lamps || []) ceilingLamp(kit, M, l, wallH - 0.02);
    let top = r.top + 0.32;
    if (p.crown) {
      const c = p.crown.at || (p.shape?.center) || pts.reduce((s, q) => [s[0] + q[0] / pts.length, s[1] + q[1] / pts.length], [0, 0]);
      const y0 = r.heightAt(c) + 0.1;
      crown(kit, M, c, y0, p.crown);
      top = Math.max(top, y0 + (p.crown.h || 2.4));
    }
    const key = p.place || null;
    if (!byPlace.has(key)) byPlace.set(key, { key, kits: [], pts: [], top: 0, label: null, area: 0, view: null });
    const pl = byPlace.get(key);
    if (p.view && !pl.view) {
      // The side the place is seen from (its front): compass or bearing, optional elevation/distance for fly-to.
      const v = typeof p.view === 'object' ? p.view : { bearing: p.view };
      const [vx, vz] = compassVec(v.bearing);
      pl.view = { bearing: (Math.atan2(vx, -vz) * 180) / Math.PI, elevation: v.elevation, distance: v.distance };
    }
    pl.kits.push(kit); pl.pts.push(...pts); pl.top = Math.max(pl.top, top);
    const area = obb(pts).area;
    if (p.labelAt) pl.label = p.labelAt;
    else if (!pl.label || area > pl.area) { pl.area = area; pl.autoLabel = pts.reduce((s, q) => [s[0] + q[0] / pts.length, s[1] + q[1] / pts.length], [0, 0]); }
  }
  const places = [...byPlace.values()].map((pl) => {
    const group = new THREE.Group();
    for (const k of pl.kits) group.add(k.build());
    const [ax, az] = pl.label || pl.autoLabel;
    const box = obb(pl.pts);
    return { key: pl.key, group, anchor: [ax, pl.top + 1.5, az], frame: { L: box.L, W: box.W }, view: pl.view };
  });

  // Building-level extras, each on the highest roof under it.
  const kit = new Kit();
  for (const c of chimneys) {
    const y = roofAt(...c.at) - 0.25, h = c.h || 1.0, r = c.r || 0.14;
    kit.cyl(M.metal, c.at[0], y + h / 2, c.at[1], r, r, h, 10);
    if (c.collar) kit.cyl(M.metal, c.at[0], y + h + 0.06, c.at[1], r * 1.9, r * 1.9, 0.12, 12);
  }
  for (const b of roofBoxes) {
    // Rooftop units (ventilation housings): dark sides, a thin snow cap, seated on the lowest roof point under them.
    const [sx, sy, sz] = b.size || [1.2, 0.7, 1.2];
    const ry = ((b.rot || 0) * Math.PI) / 180, c = Math.cos(ry), s = Math.sin(ry);
    const corners = [[-1, -1], [1, -1], [1, 1], [-1, 1]].map(([u, w]) => [b.at[0] + (u * sx * c + w * sz * s) / 2, b.at[1] + (-u * sx * s + w * sz * c) / 2]);
    const y = Math.min(...corners.map((q) => roofAt(...q))) - 0.15, hTop = Math.max(...corners.map((q) => roofAt(...q))) - y;
    const H = Math.max(sy, hTop - 0.1 + sy * 0.6);
    kit.box(M.roofEdge, b.at[0], y + H / 2, b.at[1], sx, H, sz, ry);
    kit.box(M.snow, b.at[0], y + H + 0.06, b.at[1], sx - 0.08, 0.12, sz - 0.08, ry);
  }
  const extras = kit.parts.size ? kit.build() : new THREE.Group();
  for (const f of fences) if (f.pts?.length > 1) extras.add(plainFence(buildFence(M, ground, f.pts, f, !!f.closed)));
  for (const s of signs) {
    const y = roofAt(...s.at) + (s.lift ?? 0.3);
    extras.add(signMesh(M, s, y));
    const [fx, fz] = compassVec(s.facing ?? 'N');
    const tx = -fz, tz = fx, w = s.w || 4;
    const legs = new Kit();
    for (const k of [-0.4, -0.13, 0.13, 0.4]) {
      const x = s.at[0] + tx * w * k - fx * 0.08, z = s.at[1] + tz * w * k - fz * 0.08;
      legs.box(M.metal, x, y - 0.35, z, 0.07, 0.9, 0.07);
    }
    extras.add(legs.build());
  }
  return { places, extras, top: Math.max(...places.map((p) => p.anchor[1])) };
}
